import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import {
  MagnifyingGlass, Plus, FileText, Package, Tag, TagSimple,
  Calendar, ArrowRight, FunnelSimple, ClockCounterClockwise, PencilLine,
  Warning, Truck
} from "@phosphor-icons/react";

const REPORT_TYPE_LABELS = {
  lashing: "Lashing",
  labeling: "Labeling",
  label_removal: "Label Removal",
  damaged_shipment: "Damaged Shipment",
  transloading: "Transloading",
};

const REPORT_TYPE_ICONS = {
  lashing: Package,
  labeling: Tag,
  label_removal: TagSimple,
  damaged_shipment: Warning,
  transloading: Truck,
};

const STATUS_STYLES = {
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  finalized: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [recentReports, setRecentReports] = useState([]);
  const [lastEdited, setLastEdited] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      if (typeFilter !== "all") params.report_type = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await axios.get(`${API}/reports`, { params });
      setReports(data.reports || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter, statusFilter]);

  // Fetch recent reports (top 8)
  const fetchRecent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reports`, {
        params: { page: 1, limit: 8 },
      });
      setRecentReports(data.reports || []);
    } catch {}
  }, []);

  // Fetch last edited draft
  const fetchLastEdited = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reports/last-edited`);
      setLastEdited(data);
    } catch {}
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);
  useEffect(() => { fetchRecent(); fetchLastEdited(); }, [fetchRecent, fetchLastEdited]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchReports();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Top section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Reports
            </h1>
            <p className="text-sm text-slate-500 mt-1">{total} total reports</p>
          </div>

          {/* New report buttons */}
          {user && user.role !== "viewer" && (
            <div className="flex gap-2 flex-wrap" data-testid="new-report-buttons">
              <Button onClick={() => navigate("/reports/new/lashing")} className="bg-blue-900 hover:bg-blue-800 text-white gap-2 rounded-md" data-testid="new-lashing-btn">
                <Plus size={16} weight="bold" /> Lashing
              </Button>
              <Button onClick={() => navigate("/reports/new/labeling")} variant="outline" className="border-slate-300 hover:bg-slate-100 gap-2 rounded-md" data-testid="new-labeling-btn">
                <Plus size={16} weight="bold" /> Labeling
              </Button>
              <Button onClick={() => navigate("/reports/new/label_removal")} variant="outline" className="border-slate-300 hover:bg-slate-100 gap-2 rounded-md" data-testid="new-label-removal-btn">
                <Plus size={16} weight="bold" /> Label Removal
              </Button>
              <Button onClick={() => navigate("/reports/new/damaged_shipment")} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50 gap-2 rounded-md" data-testid="new-damaged-shipment-btn">
                <Plus size={16} weight="bold" /> Damaged Shipment
              </Button>
              <Button onClick={() => navigate("/reports/new/transloading")} variant="outline" className="border-slate-300 hover:bg-slate-100 gap-2 rounded-md" data-testid="new-transloading-btn">
                <Plus size={16} weight="bold" /> Transloading
              </Button>
            </div>
          )}
        </div>

        {/* Quick Access Row: Continue + Recent Reports */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          {/* Continue last edited */}
          {lastEdited && (
            <div
              className="bg-white border-2 border-blue-200 rounded-md p-4 cursor-pointer hover:border-blue-400 hover:shadow-sm transition-all"
              onClick={() => navigate(`/reports/${lastEdited.id}/edit`)}
              data-testid="continue-last-edited"
            >
              <div className="flex items-center gap-2 mb-2">
                <PencilLine size={18} weight="fill" className="text-blue-900" />
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-900">Continue editing</span>
              </div>
              <p className="font-bold text-slate-900 text-sm truncate">{lastEdited.report_number}</p>
              <p className="text-xs text-slate-500 truncate">
                {REPORT_TYPE_LABELS[lastEdited.report_type] || lastEdited.report_type}
                {lastEdited.applicant ? ` — ${lastEdited.applicant}` : ""}
              </p>
            </div>
          )}

          {/* Recent reports */}
          <div className={`${lastEdited ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
            <div className="flex items-center gap-2 mb-3">
              <ClockCounterClockwise size={16} className="text-slate-500" />
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Recent Reports</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="recent-reports">
              {recentReports.slice(0, 8).map(r => {
                const Icon = REPORT_TYPE_ICONS[r.report_type] || FileText;
                return (
                  <div
                    key={r.id}
                    className="bg-white border border-slate-200 rounded-md p-3 cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all"
                    onClick={() => navigate(`/reports/${r.id}`)}
                    data-testid={`recent-${r.id}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon size={14} className="text-blue-900 flex-shrink-0" />
                      <span className="font-semibold text-slate-900 text-xs truncate">{r.report_number}</span>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{r.applicant || "-"}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[10px] text-slate-400">
                        {r.date_of_issuance || r.created_at?.split("T")[0] || ""}
                      </span>
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 capitalize ${STATUS_STYLES[r.status] || ""}`}>
                        {r.status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-6 flex flex-col md:flex-row gap-3 items-stretch md:items-center" data-testid="report-filters">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by report #, container #, or applicant..."
                className="pl-10 h-10 border-slate-300"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="secondary" className="h-10 px-4" data-testid="search-btn">
              Search
            </Button>
          </form>

          <div className="flex gap-2 items-center">
            <FunnelSimple size={18} className="text-slate-400" />
            <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[140px] h-10 border-slate-300" data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="lashing">Lashing</SelectItem>
                <SelectItem value="labeling">Labeling</SelectItem>
                <SelectItem value="label_removal">Label Removal</SelectItem>
                <SelectItem value="damaged_shipment">Damaged Shipment</SelectItem>
                <SelectItem value="transloading">Transloading</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[130px] h-10 border-slate-300" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="finalized">Finalized</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Reports table */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="reports-table">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-blue-900 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <FileText size={48} weight="light" />
              <p className="mt-3 text-sm">No reports found</p>
              {user && user.role !== "viewer" && (
                <Button onClick={() => navigate("/reports/new/lashing")} className="mt-4 bg-blue-900 hover:bg-blue-800 text-white gap-2">
                  <Plus size={16} /> Create your first report
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 px-4 py-3">Report #</th>
                      <th className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 px-4 py-3">Type</th>
                      <th className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 px-4 py-3">Applicant</th>
                      <th className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 px-4 py-3">Surveyor</th>
                      <th className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 px-4 py-3">Date</th>
                      <th className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 px-4 py-3">Status</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((r) => {
                      const Icon = REPORT_TYPE_ICONS[r.report_type] || FileText;
                      return (
                        <tr
                          key={r.id}
                          className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/reports/${r.id}`)}
                          data-testid={`report-row-${r.id}`}
                        >
                          <td className="px-4 py-3">
                            <span className="font-semibold text-slate-900 text-sm">{r.report_number}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Icon size={16} className="text-blue-900" />
                              <span className="text-sm text-slate-700">{REPORT_TYPE_LABELS[r.report_type] || r.report_type}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">{r.applicant || "-"}</td>
                          <td className="px-4 py-3 text-sm text-slate-700">{r.surveyor || "-"}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-sm text-slate-500">
                              <Calendar size={14} />
                              {r.date_of_issuance || r.created_at?.split("T")[0] || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className={`text-xs capitalize ${STATUS_STYLES[r.status] || ""}`}>
                              {r.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <ArrowRight size={16} className="text-slate-400" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <span className="text-xs text-slate-500">Page {page} of {pages}</span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                      data-testid="prev-page-btn"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= pages}
                      onClick={() => setPage(p => p + 1)}
                      data-testid="next-page-btn"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
